import React, { Component } from 'react'
import {
    Alert, Button, Card, Col, Descriptions, Divider, Form, Input, message, Modal, Progress,
    Row, Select, Space, Table, Tag, Typography, Upload
} from 'antd'
import { CloudUploadOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import {
    disableCodexPlusRelease, getA2aReleaseOverview, publishCodexPlusRelease,
    saveA2aPrompt, saveCodexPlusRelease, uploadCodexPlusAsset,
} from '../../request'
import './index.less'

const { TextArea } = Input
const { Text, Paragraph } = Typography
const { Option } = Select

const PLATFORM_META = [
    { value: 'macos-arm64', title: 'macOS Apple 芯片', accept: '.dmg' },
    { value: 'macos-x64', title: 'macOS Intel', accept: '.dmg' },
    { value: 'windows-x64', title: 'Windows x64', accept: '.exe,.msi' },
]
const STATUS_META = {
    draft: { text: '草稿', color: 'default' },
    published: { text: '已发布', color: 'success' },
    disabled: { text: '已停用', color: 'error' },
}

const humanSize = value => {
    const size = Number(value || 0)
    if (!size) return '-'
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / 1024 / 1024).toFixed(1)} MB`
}

export default class A2aManagement extends Component {
    releaseFormRef = React.createRef()

    state = {
        loading: false,
        error: '',
        prompt: '',
        savedPrompt: null,
        promptSaving: false,
        releases: [],
        releaseVisible: false,
        releaseSaving: false,
        editingRelease: null,
        uploadProgress: {},
        actionReleaseId: null,
    }

    componentDidMount() {
        this.loadOverview()
    }

    loadOverview = () => {
        this.setState({ loading: true, error: '' })
        return getA2aReleaseOverview().then(result => {
            const overview = result || {}
            const prompt = overview.prompt || null
            this.setState({
                savedPrompt: prompt,
                prompt: prompt ? prompt.promptContent || '' : '',
                releases: overview.releases || [],
            })
        }).catch(error => {
            this.setState({ error: error && error.message ? error.message : 'A2A管理数据加载失败' })
        }).finally(() => this.setState({ loading: false }))
    }

    savePrompt = () => {
        const prompt = this.state.prompt.trim()
        if (!prompt) return message.warning('请先填写任务发送方 Prompt')
        this.setState({ promptSaving: true })
        saveA2aPrompt({ prompt }).then(result => {
            // 2026-08-08 coder(lq): Prompt 只保留当前值，保存后 Portal 立即生效。
            message.success('Prompt 已保存并立即生效')
            this.setState({ savedPrompt: result })
        }).catch(error => this.setState({ error: error.message || 'Prompt 保存失败' }))
            .finally(() => this.setState({ promptSaving: false }))
    }

    openRelease = (release = null) => {
        // 2026-08-08 coder(lq): 弹窗使用 destroyOnClose，打开时不能在 Form 挂载前访问 ref，否则会导致页面白屏。
        this.setState({
            releaseVisible: true,
            editingRelease: release,
        })
    }

    saveRelease = () => {
        const form = this.releaseFormRef.current
        if (!form) {
            message.error('版本表单尚未准备完成，请重新打开后再试')
            return
        }
        form.validateFields().then(values => {
            this.setState({ releaseSaving: true })
            return saveCodexPlusRelease({
                id: this.state.editingRelease ? this.state.editingRelease.id : undefined,
                ...values,
            }).then(result => {
                message.success('版本草稿已保存，请继续上传安装包')
                this.setState({ releaseVisible: false, editingRelease: result })
                return this.loadOverview()
            }).catch(error => this.setState({ error: error.message || '版本草稿保存失败' }))
                .finally(() => this.setState({ releaseSaving: false }))
        })
    }

    uploadAsset = (release, platform, file) => {
        const key = `${release.id}-${platform}`
        this.setState(state => ({ uploadProgress: { ...state.uploadProgress, [key]: 1 }, error: '' }))
        uploadCodexPlusAsset(release.id, platform, file, event => {
            const percent = event.total ? Math.round(event.loaded * 100 / event.total) : 0
            this.setState(state => ({ uploadProgress: { ...state.uploadProgress, [key]: percent } }))
        }).then(() => {
            message.success(`${PLATFORM_META.find(item => item.value === platform).title} 安装包上传成功`)
            return this.loadOverview()
        }).catch(error => this.setState({ error: error.message || '安装包上传失败' }))
            .finally(() => this.setState(state => {
                const progress = { ...state.uploadProgress }
                delete progress[key]
                return { uploadProgress: progress }
            }))
        return false
    }

    confirmPublishRelease = release => {
        Modal.confirm({
            title: `确认发布 CodexPlus ${release.version}？`,
            content: `发布到 ${release.channel === 'beta' ? 'Beta' : '稳定'} 通道后，客户端自动更新会读取这个版本，同通道旧版本将停用。`,
            okText: '确认发布',
            onOk: () => {
                this.setState({ actionReleaseId: release.id })
                return publishCodexPlusRelease({ id: release.id }).then(() => {
                    message.success('CodexPlus 版本已发布')
                    return this.loadOverview()
                }).catch(error => {
                    this.setState({ error: error.message || '版本发布失败' })
                    throw error
                }).finally(() => this.setState({ actionReleaseId: null }))
            },
        })
    }

    confirmDisableRelease = release => {
        Modal.confirm({
            title: `停用 CodexPlus ${release.version}？`,
            content: '停用后客户端与 Portal 都不会再获取到这个版本。',
            okText: '确认停用',
            okButtonProps: { danger: true },
            onOk: () => disableCodexPlusRelease({ id: release.id }).then(() => {
                message.success('版本已停用')
                return this.loadOverview()
            }),
        })
    }

    renderAssets = release => {
        const assets = release.assets || []
        if (release.status !== 'draft') {
            return <Space direction="vertical" size={4}>{assets.map(asset => (
                <Text key={asset.platform}>{asset.platform}：{asset.fileName}（{humanSize(asset.fileSize)}）</Text>
            ))}</Space>
        }
        return <Row gutter={[12, 12]}>{PLATFORM_META.map(platform => {
            const asset = assets.find(item => item.platform === platform.value)
            const key = `${release.id}-${platform.value}`
            const progress = this.state.uploadProgress[key]
            return <Col xs={24} lg={8} key={platform.value}>
                <div className="a2a-asset-box">
                    <Text strong>{platform.title}</Text>
                    <Paragraph ellipsis={{ rows: 2 }} type={asset ? undefined : 'secondary'}>
                        {asset ? `${asset.fileName} · ${humanSize(asset.fileSize)}` : `未上传（${platform.accept}）`}
                    </Paragraph>
                    {progress !== undefined ? <Progress percent={progress} size="small" /> : (
                        <Upload accept={platform.accept} showUploadList={false}
                            beforeUpload={file => this.uploadAsset(release, platform.value, file)}>
                            <Button size="small" icon={<CloudUploadOutlined />}>{asset ? '替换安装包' : '上传安装包'}</Button>
                        </Upload>
                    )}
                    {asset && <Text className="a2a-sha" type="secondary">SHA-256：{asset.sha256}</Text>}
                </div>
            </Col>
        })}</Row>
    }

    columns = [
        { title: '版本', dataIndex: 'version', width: 130, render: value => <Text strong>{value}</Text> },
        { title: '通道', dataIndex: 'channel', width: 90, render: value => <Tag color={value === 'beta' ? 'orange' : 'blue'}>{value}</Tag> },
        { title: '状态', dataIndex: 'status', width: 100, render: value => { const meta = STATUS_META[value] || {text:value}; return <Tag color={meta.color}>{meta.text}</Tag> } },
        { title: '安装包', key: 'assets', render: (_, release) => this.renderAssets(release) },
        { title: '发布说明', dataIndex: 'releaseNotes', width: 220, render: value => <Paragraph ellipsis={{rows:3}}>{value || '-'}</Paragraph> },
        { title: '操作', key: 'action', width: 170, fixed: 'right', render: (_, release) => <Space>
            {release.status === 'draft' && <Button type="link" size="small" onClick={() => this.openRelease(release)}>编辑</Button>}
            {release.status === 'draft' && <Button type="link" size="small" loading={this.state.actionReleaseId === release.id} onClick={() => this.confirmPublishRelease(release)}>发布</Button>}
            {release.status === 'published' && <Button type="link" danger size="small" onClick={() => this.confirmDisableRelease(release)}>停用</Button>}
        </Space> },
    ]

    render() {
        const { loading, error, savedPrompt } = this.state
        return <div className="a2a-management">
            <div className="a2a-page-head">
                <div><h2>A2A 管理</h2><Text type="secondary">集中管理任务发送说明、CodexPlus 安装包和自动更新版本。</Text></div>
                <Button icon={<ReloadOutlined />} loading={loading} onClick={this.loadOverview}>刷新</Button>
            </div>
            {error && <Alert closable type="error" showIcon message="操作没有完成" description={error} onClose={() => this.setState({error:''})} />}

            <Card title="任务发送方 Prompt" loading={loading}>
                <Alert type="info" showIcon message="这段内容会显示给需要从服务端派发 A2A 任务的用户"
                    description="可以写清 MCP 安装、环境变量设置、任务字段和发送示例。保存后 Portal A2A 页面立即使用新内容。" />
                <Descriptions size="small" column={1} className="a2a-meta">
                    <Descriptions.Item label="当前状态">{savedPrompt ? `已生效 · ${savedPrompt.updateTime || '-'}` : '尚未配置'}</Descriptions.Item>
                </Descriptions>
                <TextArea value={this.state.prompt} rows={16} onChange={event => this.setState({prompt:event.target.value})}
                    placeholder="填写任务发送方从安装 MCP 到发送任务的完整说明……" />
                <Space className="a2a-actions">
                    <Button type="primary" loading={this.state.promptSaving} onClick={this.savePrompt}>保存并立即生效</Button>
                </Space>
            </Card>

            <Card title="CodexPlus 版本与安装包" loading={loading}
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => this.openRelease()}>新建版本</Button>}>
                <Alert type="warning" showIcon message="发布后会用于客户端自动更新"
                    description="请至少上传一个平台安装包。稳定版与 Beta 版互不覆盖；安装包下载使用短时签名链接。" />
                <Table rowKey="id" columns={this.columns} dataSource={this.state.releases} pagination={false}
                    scroll={{x:1200}} className="a2a-release-table" />
            </Card>

            <Modal title={this.state.editingRelease ? '编辑版本草稿' : '新建版本草稿'} visible={this.state.releaseVisible}
                okText="保存草稿" cancelText="取消" confirmLoading={this.state.releaseSaving} onOk={this.saveRelease}
                onCancel={() => this.setState({releaseVisible:false})} destroyOnClose>
                <Form
                    key={this.state.editingRelease ? `edit-${this.state.editingRelease.id}` : 'new'}
                    ref={this.releaseFormRef}
                    layout="vertical"
                    initialValues={{
                        version: this.state.editingRelease ? this.state.editingRelease.version : '',
                        channel: this.state.editingRelease ? this.state.editingRelease.channel : 'stable',
                        releaseNotes: this.state.editingRelease ? this.state.editingRelease.releaseNotes : '',
                    }}
                >
                    <Form.Item name="version" label="版本号" rules={[{required:true,message:'请填写版本号'},{pattern:/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/,message:'例如 1.2.3 或 1.2.3-beta.1'}]}>
                        <Input placeholder="例如 1.2.3" />
                    </Form.Item>
                    <Form.Item name="channel" label="发布通道" rules={[{required:true}]}>
                        <Select><Option value="stable">稳定版 stable</Option><Option value="beta">测试版 beta</Option></Select>
                    </Form.Item>
                    <Form.Item name="releaseNotes" label="发布说明"><TextArea rows={6} placeholder="说明本次更新内容和注意事项" /></Form.Item>
                </Form>
            </Modal>
        </div>
    }
}
